import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { TournamentsTypes } from 'src/services/tournaments/addTournament';
import { CategoriesGender } from 'src/services/tournaments/getTournaments';
import * as yup from 'yup';

export const useTournamentFormValidationSchema = () => {
  const { t } = useTranslation();

  const genders = [
    {
      title: t('TournamentsScreen.male'),
      id: CategoriesGender.male,
    },
    { title: t('TournamentsScreen.female'), id: CategoriesGender.female },
    { title: t('TournamentsScreen.mixed'), id: CategoriesGender.mixed },
  ];

  const tournamentsTypes = [
    {
      id: TournamentsTypes.ROUND_ROBIN,
      title: t('TournamentsScreen.roundRobin'),
    },
    {
      id: TournamentsTypes.DOUBLE_ROUND_ROBIN,
      title: t('TournamentsScreen.doubleRoundRobin'),
    },
    {
      id: TournamentsTypes.SINGLE_ELIMINATION,
      title: t('TournamentsScreen.singleElimintation'),
    },
    {
      id: TournamentsTypes.ROUND_ROBIN_SINGLE_ELIMINATION,
      title: t('TournamentsScreen.roundRobinSingleElimination'),
    },
    {
      id: TournamentsTypes.CUSTOM,
      title: t('TournamentsScreen.custom'),
    },
  ];

  const validationSchema = (tabIndex: number, tournamentTypeId?: number) => {
    const validation = yup.object().shape({
      name: yup.string().required(t('TournamentsScreen.tournamentNameRequired')),
      name_ar: yup.string().required(t('TournamentsScreen.tournamentNameArabicRequired')),
      tournament_type_id: yup.number().required(t('TournamentsScreen.selectTournamentType')),

      teams_per_group: yup.string().when('tournament_type_id', {
        is: (tournament_type_id: TournamentsTypes) =>
          tournament_type_id === TournamentsTypes.ROUND_ROBIN_SINGLE_ELIMINATION ? true : false,
        then: yup.string().required(t('TournamentsScreen.enterNumberOfTeamsPerGroup')),
        otherwise: yup.string().nullable(),
      }),
      teams_to_qualify_per_group: yup.string().when('tournament_type_id', {
        is: (tournament_type_id: TournamentsTypes) =>
          tournament_type_id === TournamentsTypes.ROUND_ROBIN_SINGLE_ELIMINATION ? true : false,
        then: yup.string().required(t('TournamentsScreen.enterNumberOfTeamsToQualifyPerGroup')),
        otherwise: yup.string().nullable(),
      }),
      sport_type_id: yup.number().required(t('TournamentsScreen.selectSportType')),
      categories: yup
        .array()
        .min(1, t('TournamentsScreen.selectAtLeastOneCategory'))
        .required(t('TournamentsScreen.selectAtLeastOneCategory')),
      start_date: yup.date().required(t('TournamentsScreen.startDateRequired')),
      end_date: yup
        .date()
        .when('start_date', (start_date, yup) => {
          if (start_date) {
            return yup.min(
              start_date,
              t('TournamentsScreen.endDateShouldBeAfterOrSameAsStartDate'),
            );
          }

          return;
        })
        .required(t('TournamentsScreen.endDateRequired')),
      registration_deadline: yup
        .date()
        .when('start_date', (start_date, yup) => {
          if (start_date) {
            return (
              start_date &&
              yup.max(start_date, t('TournamentsScreen.deadlineShouldBeBeforeStartingDate'))
            );
          }

          return;
        })
        .required(t('TournamentsScreen.registrationDeadlineRequired')),
      price_per_team: yup.string().required(t('TournamentsScreen.pricePerTeamRequired')),
      supported_payment_methods: yup
        .array()
        .min(1, t('TournamentsScreen.atLeastOnePaymentMethod'))
        .required(t('TournamentsScreen.atLeastOnePaymentMethod')),

      gender: yup.string().required(t('TournamentsScreen.genderRequired')),

      prizes:
        tabIndex === 1
          ? yup.string().min(1).required(t('TournamentsScreen.enterPrize'))
          : yup.string(),
      daily_timings: yup.string().nullable(),
      additional_info: yup.string().nullable(),
    });

    return validation;
  };

  return { validationSchema, genders, tournamentsTypes };
};
